import React, { useEffect, useState } from 'react';
import {
    NSCard, NSDropdown, NSInput, NSLabel,
} from 'bricks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLeftRight, faDollar, faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import './Uses.scss';
import { numeralFormatter, numeralFormatterCurrency } from 'ns_libs/formatter';
import { Tooltip } from 'reactstrap';
import classNames from 'classnames';
import { calculateDeveloperFeePercent, calculateTotalDeveloperFee } from 'views/ProFormaWizard/helpers/calculateTotalDeveloperFeeHelper';
import { ProFormaModelIds } from 'constants/proForma';
import { FormikErrors, FormikTouched } from 'formik';
import { IWizardStepTwoUses } from 'views/ProFormaWizard/types';
import { COST_BASIS_OPTIONS, COST_BASIS_TYPES } from 'views/DealDashboard/BOE/components/SlideoutForm/constants';
import BOEDiscrepancyIcon from 'views/ProFormaTable/components/BOEDiscrepancyIcon/BOEDiscrepancyIcon';
import { DEVELOPMENT_EXPANDABLE_SECTIONS } from 'views/ProFormaTable/constants';
import { BOEProFormaDiscrepancies } from 'views/ProFormaTable/types';
import { isDebtSource } from 'utils/sourceTypeUtils';
import { useGetSourceTypes } from 'queries/ProFormaSource';
import RenderIf from 'components/RenderIf/RenderIf';
import IconWithTooltip from 'components/IconWithTooltip/IconWithTooltip';
import { faLightbulbGear } from '@fortawesome/pro-solid-svg-icons';
import { INTEREST_RESERVE_WIZARD_TEXT } from 'views/ProFormaWizard/constants';
import ReadOnlyWrapper from 'components/ReadOnlyWrapper/ReadOnlyWrapper';
import { StepTwoValues } from '../ProFormaWizardStepTwo';

export interface IUsesProps {
    dealUnits?: number;
    wizardBuiltGrossSF?: number;
    proFormaModelId?: number;
    values: StepTwoValues;
    totalSources: number;
    onChange: (key: string, value: number | string) => void;
    onBlur: (key: string) => void;
    errors?: FormikErrors<IWizardStepTwoUses>;
    touched?: FormikTouched<IWizardStepTwoUses>;
    boeDiscrepancies?: BOEProFormaDiscrepancies;
}

const Uses = ({
    dealUnits, wizardBuiltGrossSF, proFormaModelId, values, totalSources,
    onChange, onBlur, errors, touched, boeDiscrepancies,
}: IUsesProps) => {
    const [tooltipOpen, setTooltipOpen] = useState(false);
    const toggleTooltip = () => setTooltipOpen(!tooltipOpen);
    const { data: sourceTypes } = useGetSourceTypes();

    const debtSources = values.sources?.filter(source => isDebtSource(source.sourceTypeId, sourceTypes));

    const filteredUnits
        = [ProFormaModelIds.APARTMENT, ProFormaModelIds.APARTMENT_WITH_RETAIL].includes(proFormaModelId as ProFormaModelIds)
            ? COST_BASIS_OPTIONS : COST_BASIS_OPTIONS.filter(unit => unit.value !== COST_BASIS_TYPES.PER_UNIT);
    const totalUses = values.acquisitionCosts + values.totalHardCosts + values.totalSoftCosts + values.interestReserve + values.developerFee;

    const updateCosts = (
        type: 'hardCosts' | 'totalHardCosts' | 'softCosts' | 'totalSoftCosts',
        newValue: number,
        relatedType: 'totalHardCosts' | 'hardCosts' | 'totalSoftCosts' | 'softCosts',
        multiplierType: 'hardCostType' | 'softCostType',
    ) => {
        const multiplier = values[multiplierType] === COST_BASIS_TYPES.PER_UNIT ? dealUnits : wizardBuiltGrossSF;

        if (type.includes('total')) {
            const baseValue = Number(numeralFormatter(newValue / (multiplier ?? 1), '0.00'));
            onChange(relatedType, baseValue);
            onChange(type, newValue);
        } else {
            const totalValue = Number(numeralFormatter(newValue * (multiplier ?? 1), '0.00'));
            onChange(type, newValue);
            onChange(relatedType, totalValue);
        }
    };

    const updateDeveloperFeePercent = (value: number) => {
        onChange('developerFeePercent', value);
        const newDeveloperFee = calculateTotalDeveloperFee({ ...values, developerFeePercent: value });
        onChange('developerFee', newDeveloperFee);
    };

    const updateDeveloperFee = (value: number) => {
        onChange('developerFee', value);
        const newDeveloperFeePercent = calculateDeveloperFeePercent({ ...values, developerFee: value });
        onChange('developerFeePercent', newDeveloperFeePercent);
    };

    useEffect(() => {
        updateCosts('hardCosts', values.hardCosts, 'totalHardCosts', 'hardCostType');
    }, [values.hardCostType]);

    useEffect(() => {
        updateCosts('softCosts', values.softCosts, 'totalSoftCosts', 'softCostType');
    }, [values.softCostType]);

    useEffect(() => {
        const newDeveloperFee = calculateTotalDeveloperFee(values);
        onChange('developerFee', newDeveloperFee);
    }, [values.acquisitionCosts, values.interestReserve, values.hardCosts, values.softCosts, values.totalHardCosts, values.totalSoftCosts]);

    useEffect(() => {
        if (values.developerFee !== 0 && values.developerFeePercent === 0) {
            const newDeveloperFeePercent = calculateDeveloperFeePercent(values);
            onChange('developerFeePercent', newDeveloperFeePercent);
        }
        if (values.developerFeePercent && values?.developerFeePercent > 0 && values.developerFee === 0) {
            const newDeveloperFee = calculateTotalDeveloperFee(values);
            onChange('developerFee', newDeveloperFee);
        }
    }, [values.developerFee, values.developerFeePercent]);

    useEffect(() => {
        if (!debtSources.length && Boolean(values.interestReserve)) {
            onChange('interestReserve', 0);
        }
    }, [debtSources.length]);

    return (
        <div className="w-100">
            <small>1 of 2</small>
            <div className="d-flex">
                <div>
                    <div className="Uses__title">Uses</div>
                    <div className="Uses__subtitle pb-2">
                        Add any information about this investment's uses. You will be able to add more detailed information later.
                    </div>
                </div>
                <RenderIf isTrue={boeDiscrepancies ? !!boeDiscrepancies[DEVELOPMENT_EXPANDABLE_SECTIONS[1].number]?.length : false}>
                    <BOEDiscrepancyIcon
                        section={DEVELOPMENT_EXPANDABLE_SECTIONS[1].title}
                        metricNames={boeDiscrepancies ? boeDiscrepancies[DEVELOPMENT_EXPANDABLE_SECTIONS[1].number] : []}
                        className="ml-2 my-auto"
                    />
                </RenderIf>
            </div>
            <div className="d-flex flex-row pb-2">
                <NSCard className="NSCard--level-2 p-2 mr-2 w-50 ml-0 mr-0">
                    <NSInput
                        id="acquisitionCosts"
                        type="number"
                        label="Acquisition costs"
                        name="acquisitionCosts"
                        placeholder="Enter amount"
                        prependInputAddon={<FontAwesomeIcon icon={faDollar} />}
                        value={values.acquisitionCosts || ''}
                        onChange={(e: { target: { value: number } }) => onChange('acquisitionCosts', Number(e.target.value))}
                        onBlur={() => onBlur('acquisitionCosts')}
                        showErrorMsg={touched?.acquisitionCosts && !!errors?.acquisitionCosts}
                        errorMsg={touched?.acquisitionCosts ? errors?.acquisitionCosts : ''}
                    />
                </NSCard>
                <NSCard className="NSCard--level-2 p-2 w-50 ml-0 mr-0">
                    <NSLabel className="text-dark font-weight-normal" htmlFor="hardCosts">
                        Hard costs
                    </NSLabel>
                    <div className="d-flex flex-row justify-content-center align-items-baseline">
                        <div className="d-flex flex-column">
                            <NSInput
                                id="hardCosts"
                                dataTestId="hardCosts"
                                type="number"
                                name="hardCosts"
                                placeholder="Enter amount"
                                prependInputAddon={<FontAwesomeIcon icon={faDollar} />}
                                value={values.hardCosts != null ? Number(numeralFormatter(values.hardCosts, '0.00')) : ''}
                                onChange={(e: { target: { value: number } }) => {
                                    updateCosts('hardCosts', Number(e.target.value), 'totalHardCosts', 'hardCostType');
                                }}
                                appendInputAddon={(
                                    <NSDropdown
                                        options={filteredUnits}
                                        selectedOption={filteredUnits.find(option => option.value === values.hardCostType)}
                                        customSelectedOptionText={filteredUnits.find(option => option.value === values.hardCostType)?.label}
                                        handleSelectOption={option => onChange('hardCostType', option.value)}
                                        badgeColor=""
                                        isBadgeToggle
                                        toggleClassName="p-0 m-0"
                                    />
                                )}
                                onBlur={() => onBlur('hardCosts')}
                                showErrorMsg={touched?.hardCosts && !!errors?.hardCosts}
                                errorMsg={touched?.hardCosts ? errors?.hardCosts : ''}
                            />
                        </div>
                        <FontAwesomeIcon icon={faLeftRight} className="mx-2" />
                        <div className="d-flex flex-column">
                            <NSInput
                                id="totalHardCosts"
                                type="number"
                                name="totalHardCosts"
                                value={values.totalHardCosts != null ? values.totalHardCosts : ''}
                                onChange={(e: { target: { value: number } }) => {
                                    updateCosts('totalHardCosts', Number(e.target.value), 'hardCosts', 'hardCostType');
                                }}
                                prependInputAddon={<FontAwesomeIcon icon={faDollar} />}
                                appendInputAddon={<div>Total</div>}
                                onBlur={() => {
                                    onBlur('totalHardCosts');
                                    onBlur('hardCosts');
                                }}
                                showErrorMsg={touched?.totalHardCosts && !!errors?.totalHardCosts}
                                errorMsg={touched?.totalHardCosts ? errors?.totalHardCosts : ''}
                            />
                        </div>
                    </div>
                </NSCard>
            </div>
            <div className="d-flex flex-row pb-2">
                <NSCard className="NSCard--level-2 p-2 mr-2 w-50 ml-0 mr-0">
                    <NSLabel className="text-dark font-weight-normal">Soft costs</NSLabel>
                    <div className="d-flex flex-row justify-content-center align-items-baseline">
                        <div className="d-flex flex-column">
                            <NSInput
                                id="softCosts"
                                dataTestId="softCosts"
                                type="number"
                                name="softCosts"
                                placeholder="Enter amount"
                                prependInputAddon={<FontAwesomeIcon icon={faDollar} />}
                                value={values.softCosts != null ? Number(numeralFormatter(values.softCosts, '0.00')) : ''}
                                onChange={(e: { target: { value: number } }) => {
                                    updateCosts('softCosts', Number(e.target.value), 'totalSoftCosts', 'softCostType');
                                }}
                                appendInputAddon={(
                                    <NSDropdown
                                        options={filteredUnits}
                                        selectedOption={filteredUnits.find(option => option.value === values.softCostType)}
                                        customSelectedOptionText={filteredUnits.find(option => option.value === values.softCostType)?.label}
                                        handleSelectOption={option => onChange('softCostType', option.value)}
                                        badgeColor=""
                                        isBadgeToggle
                                        toggleClassName="p-0 m-0"
                                    />
                                )}
                                onBlur={() => onBlur('softCosts')}
                                showErrorMsg={touched?.softCosts && !!errors?.softCosts}
                                errorMsg={touched?.softCosts ? errors?.softCosts : ''}
                            />
                        </div>
                        <FontAwesomeIcon icon={faLeftRight} className="mx-2" />
                        <div className="d-flex flex-column">
                            <NSInput
                                id="totalSoftCosts"
                                type="number"
                                name="totalSoftCosts"
                                value={values.totalSoftCosts != null ? values.totalSoftCosts : ''}
                                onChange={(e: { target: { value: number } }) => {
                                    updateCosts('totalSoftCosts', Number(e.target.value), 'softCosts', 'softCostType');
                                }}
                                prependInputAddon={<FontAwesomeIcon icon={faDollar} />}
                                appendInputAddon={<div>Total</div>}
                                onBlur={() => {
                                    onBlur('totalSoftCosts');
                                    onBlur('softCosts');
                                }}
                                showErrorMsg={touched?.totalSoftCosts && !!errors?.totalSoftCosts}
                                errorMsg={touched?.totalSoftCosts ? errors?.totalSoftCosts : ''}
                            />
                        </div>
                    </div>
                </NSCard>
                <div className="d-flex flex-row w-50">
                    <NSCard className="NSCard--level-2 px-2">
                        <div className="d-flex flex-row justify-content-between pt-2">
                            <div>
                                <NSLabel className="text-dark font-weight-normal">Developer fee</NSLabel>
                                <FontAwesomeIcon className="px-1 text-white" id="developer-fee-tooltip" icon={faCircleInfo} />
                                <Tooltip target="developer-fee-tooltip" isOpen={tooltipOpen} toggle={toggleTooltip} placement="top">
                                    Developer fee is calculated on total uses.
                                </Tooltip>
                            </div>
                            <div>
                                <IconWithTooltip
                                    name="interestReserveIconWizard-devFee"
                                    icon={faLightbulbGear}
                                    tooltipText={INTEREST_RESERVE_WIZARD_TEXT}
                                    className="my-auto"
                                />
                            </div>
                        </div>
                        <div className="d-flex flex-row justify-content-center align-items-baseline">
                            <div className="Uses__developer-fee-percent">
                                <NSInput
                                    id="developerFeePercent"
                                    dataTestId="developerFeePercent"
                                    inputClassName="pr-2"
                                    type="number"
                                    name="developerFeePercent"
                                    placeholder="Enter percentage"
                                    // We have to multiply by 100 because we needed for the calculations.
                                    value={Number(values.developerFeePercent) * 100 || ''}
                                    onChange={(e: { target: { value: number } }) => updateDeveloperFeePercent(Number(e.target.value) / 100)}
                                    appendInputAddon={<div>%</div>}
                                    onBlur={() => onBlur('developerFeePercent')}
                                    showErrorMsg={touched?.developerFeePercent && !!errors?.developerFeePercent}
                                    errorMsg={touched?.developerFeePercent ? errors?.developerFeePercent : ''}
                                />
                            </div>
                            <div className="Uses__developer-fee-amount">
                                <NSInput
                                    id="developerFee"
                                    type="number"
                                    name="developerFee"
                                    value={values.developerFee}
                                    onChange={(e: { target: { value: number } }) => updateDeveloperFee(Number(e.target.value))}
                                    prependInputAddon={<FontAwesomeIcon icon={faDollar} />}
                                    onBlur={() => onBlur('developerFee')}
                                />
                            </div>
                        </div>
                    </NSCard>
                    <NSCard className={classNames('flex-grow-1 ml-2 p-2', {
                        'NSCard--level-2': Boolean(debtSources.length),
                        'text-dark': Boolean(debtSources.length),
                        'NSCard--level-1': !debtSources.length,
                    })}
                    >
                        <div className="d-flex justify-content-between">
                            <NSLabel className="font-weight-normal">Interest reserve</NSLabel>
                            <IconWithTooltip
                                name="interestReserveIconWizard-interestReserve"
                                icon={faLightbulbGear}
                                tooltipText={INTEREST_RESERVE_WIZARD_TEXT}
                                className="my-auto"
                            />
                        </div>
                        <ReadOnlyWrapper isReadOnly={!debtSources.length}>
                            <NSInput
                                id="interestReserve"
                                dataTestId="interestReserve"
                                type="number"
                                name="interestReserve"
                                placeholder="Enter amount"
                                prependInputAddon={<FontAwesomeIcon icon={faDollar} />}
                                value={values.interestReserve != null ? values.interestReserve : ''}
                                onChange={(e: { target: { value: number } }) => onChange('interestReserve', Number(e.target.value))}
                                onBlur={() => onBlur('interestReserve')}
                                showErrorMsg={touched?.interestReserve && !!errors?.interestReserve}
                                errorMsg={touched?.interestReserve ? errors?.interestReserve : ''}
                            />
                        </ReadOnlyWrapper>
                    </NSCard>
                </div>
            </div>
            <NSCard
                className={classNames('p-2 mt-1 justify-content-center', {
                    Uses__total: totalSources >= totalUses,
                    'Uses__total--highlight': totalSources < totalUses,
                })}
            >
                <div className="d-flex justify-content-between align-items-center">
                    <span className="font-weight-bold text-white">Total uses:</span>

                    <div className="d-flex align-items-center justify-content-center">
                        <IconWithTooltip
                            name="interestReserveIconWizard-totalUses"
                            icon={faLightbulbGear}
                            tooltipText={INTEREST_RESERVE_WIZARD_TEXT}
                            className="my-auto mr-2"
                        />

                        {totalSources < totalUses ? <NSLabel className="Uses__label-warning Uses__total-label">Uses exceed sources</NSLabel> : null}
                        <span className="font-weight-bold text-white">{numeralFormatterCurrency(totalUses)}</span>
                    </div>
                </div>
            </NSCard>
        </div>
    );
};

export default Uses;
